import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { List, AutoSizer } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { excerptOf } from './MachineTranslationDialog';

// Picks fields of one item for deletion: a filter box, select-all / untranslated / none over
// the filtered rows, a virtualized table of source and translation with a tick per row, and a
// second confirm naming the count. The caller deletes; the dialog only hands over the ids.
//
// fields: [{id, source, target}]; onConfirm(ids); onClose()
const DeleteFieldsDialog = ({ itemTitle, fields, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = React.useState('');
  const [selected, setSelected] = React.useState(() => new Set());
  const [confirming, setConfirming] = React.useState(false);
  // the rows lose the list's vertical scrollbar width; the header gets it as padding so the
  // columns line up
  const [scrollbarWidth, setScrollbarWidth] = React.useState(0);

  const query = filter.trim().toLowerCase();
  const rows = React.useMemo(() => {
    // the filter matches the full texts, not the excerpts: an image URL's file name sits past the cut
    const all = (fields || []).map(field => ({
      id: field.id,
      source: excerptOf(field.source),
      target: excerptOf(field.target),
      haystack: `${String(field.source ?? '')}\n${String(field.target ?? '')}`.toLowerCase(),
      untranslated: !String(field.target || '').trim(),
    }));
    if(!query) return all;
    return all.filter(row => row.haystack.includes(query));
  }, [fields, query]);

  const shownIds = rows.map(row => row.id);
  const allShownSelected = shownIds.length > 0 && shownIds.every(id => selected.has(id));
  const select = (ids) => setSelected(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
  const toggle = (id) => setSelected(prev => { const next = new Set(prev); if(next.has(id)) next.delete(id); else next.add(id); return next; });
  const count = selected.size;

  const ROW_HEIGHT = 28;
  const LIST_HEIGHT = 300;
  const textCol = { flex: '1 1 0', minWidth: 0 };
  const tickCol = { flex: '0 0 34px' };
  const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const cell = (extra = {}) => ({ display: 'flex', alignItems: 'center', minWidth: 0, px: 0.75, height: '100%', ...extra });
  const head = { color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' };

  const renderRow = (row, key, style) => (
    <Box key={key} style={style} onClick={() => toggle(row.id)}
      sx={{ display: 'flex', alignItems: 'stretch', borderRadius: '4px', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
      <Box component="span" sx={cell({ ...textCol, color: 'text.primary' })}>
        <Box component="span" sx={ellipsis}>{row.source}</Box>
      </Box>
      <Box component="span" sx={cell({ ...textCol, color: row.untranslated ? 'text.disabled' : 'text.primary' })}>
        <Box component="span" sx={ellipsis}>{row.untranslated ? t('itemsList.deleteFieldsUntranslated') : row.target}</Box>
      </Box>
      <Box component="span" sx={cell({ ...tickCol, justifyContent: 'center', px: 0 })}>
        <Checkbox size="small" sx={{ p: 0.25 }} checked={selected.has(row.id)} onChange={() => toggle(row.id)} onClick={e => e.stopPropagation()} />
      </Box>
    </Box>
  );

  return (
    <React.Fragment>
      <Dialog maxWidth="md" fullWidth onClose={onClose} aria-labelledby="delete-fields-title" open={!confirming}>
        <DialogTitle id="delete-fields-title">
          <Typography variant="h5" component="span">{t('itemsList.deleteFieldsTitle', { item: itemTitle })}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>{t('itemsList.deleteFieldsSubtitle')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <TextField
              size="small"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder={t('itemsList.deleteFieldsFilter')}
              autoFocus
              sx={{ flex: '1 1 220px', minWidth: 180 }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment> } }}
            />
            {/* the shortcuts act on the filtered rows, so "select all" after a filter picks that subset */}
            <Button size="small" variant="text" disabled={allShownSelected} onClick={() => select(shownIds)}>{t('itemsList.deleteFieldsSelectAll')}</Button>
            <Button size="small" variant="text" disabled={!rows.some(row => row.untranslated && !selected.has(row.id))} onClick={() => select(rows.filter(row => row.untranslated).map(row => row.id))}>{t('itemsList.deleteFieldsSelectUntranslated')}</Button>
            <Button size="small" variant="text" disabled={count === 0} onClick={() => setSelected(new Set())}>{t('itemsList.deleteFieldsSelectNone')}</Button>
          </Box>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            {t('itemsList.deleteFieldsShown', { shown: rows.length, total: (fields || []).length, selected: count })}
          </Typography>
          <Box sx={{ fontSize: 13 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', pr: `${scrollbarWidth}px` }}>
              <Box component="span" sx={{ ...head, ...textCol, px: 0.75, py: 0.25 }}>{t('itemsList.fieldTableSource')}</Box>
              <Box component="span" sx={{ ...head, ...textCol, px: 0.75, py: 0.25 }}>{t('itemsList.fieldTableTarget')}</Box>
              <Box component="span" sx={tickCol} />
            </Box>
            {rows.length === 0
              ? <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>{t('itemsList.deleteFieldsNoMatch')}</Typography>
              : <AutoSizer disableHeight>
                  {({ width }) => (
                    <List
                      width={width}
                      height={Math.min(LIST_HEIGHT, rows.length * ROW_HEIGHT)}
                      rowCount={rows.length}
                      rowHeight={ROW_HEIGHT}
                      overscanRowCount={10}
                      onScrollbarPresenceChange={({ vertical, size }) => setScrollbarWidth(vertical ? size : 0)}
                      // the selection lives outside the list; the prop makes it repaint on a tick
                      selectedCount={count}
                      rowRenderer={({ index, key, style }) => renderRow(rows[index], key, style)}
                    />
                  )}
                </AutoSizer>
            }
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose} size="medium">{t('system.cancel')}</Button>
          <Button variant="contained" color="error" size="medium" disabled={count === 0} onClick={() => setConfirming(true)}>
            {t('itemsList.deleteFieldsRun', { count })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth="xs" fullWidth open={confirming} onClose={() => setConfirming(false)} aria-labelledby="delete-fields-confirm-title">
        <DialogTitle id="delete-fields-confirm-title">{t('itemsList.deleteFieldsConfirmTitle', { count })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('itemsList.deleteFieldsConfirmDescription')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirming(false)} size="medium">{t('system.cancel')}</Button>
          <Button variant="contained" color="error" size="medium" onClick={() => onConfirm([...selected])}>{t('itemsList.deleteFieldsConfirm')}</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default DeleteFieldsDialog;
